import {
  EWebAnalyticsCompareMode,
  EWebAnalyticsRecommendationStatus,
  EWebAnalyticsSortDirection,
  EWebAnalyticsTableKind,
} from '@mahawi/eshop-common';
import { createSlice, type Reducer } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import { resolvePresetPeriod, WEB_ANALYTICS_DEFAULT_PRESET } from './period';
import {
  type IWebAnalyticsInsightsState,
  type IWebAnalyticsOverviewRequest,
  type IWebAnalyticsOverviewResponse,
  type IWebAnalyticsProviderStatusResponse,
  type IWebAnalyticsQueryPagesRequest,
  type IWebAnalyticsQueryPagesResponse,
  type IWebAnalyticsRecommendationsRequest,
  type IWebAnalyticsRecommendationsResponse,
  type IWebAnalyticsTableRequest,
  type IWebAnalyticsTableResponse,
  type IWebAnalyticsUpdateRecommendationRequest,
  type IWebAnalyticsUpdateRecommendationResponse,
} from './types';

export const preloadedState: IWebAnalyticsInsightsState = {
  ...asyncStateInitial,
  providers: undefined,
  lastRun: undefined,
  query: resolvePresetPeriod(
    WEB_ANALYTICS_DEFAULT_PRESET,
    EWebAnalyticsCompareMode.PREVIOUS_PERIOD,
  ),
  overview: undefined,
  overviewInProcess: false,
  tableSettings: {
    kind: EWebAnalyticsTableKind.PAGES,
    sortBy: 'clicks',
    sortDir: EWebAnalyticsSortDirection.DESC,
    page: 1,
    pageSize: 25,
  },
  table: undefined,
  tableInProcess: false,
  queryPagesRequest: undefined,
  queryPages: undefined,
  queryPagesInProcess: false,
  recommendationsRequest: {
    status: [EWebAnalyticsRecommendationStatus.OPEN],
    page: 1,
    pageSize: 25,
  },
  recommendations: undefined,
  recommendationsInProcess: false,
  updatingRecommendationUUID: undefined,
};

const webAnalyticsInsightsSlice = createSlice({
  name: 'WebAnalyticsInsights',
  initialState: preloadedState,
  reducers: {
    loadProviderStatus(state) {
      startLoading(state);
    },
    loadProviderStatusResponse: {
      reducer(state, action: { payload: IWebAnalyticsProviderStatusResponse }) {
        state.providers = action.payload.providers;
        state.lastRun = action.payload.lastRun;
        finishLoading(state);
      },
      prepare(response: IWebAnalyticsProviderStatusResponse) {
        return { payload: response };
      },
    },
    loadOverview: {
      reducer(state, action: { payload: IWebAnalyticsOverviewRequest }) {
        state.query = action.payload;
        state.overviewInProcess = true;
      },
      prepare(request: IWebAnalyticsOverviewRequest) {
        return { payload: request };
      },
    },
    loadOverviewResponse: {
      reducer(state, action: { payload: IWebAnalyticsOverviewResponse }) {
        state.overview = action.payload;
        state.providers = action.payload.providerStatus;
        state.overviewInProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(response: IWebAnalyticsOverviewResponse) {
        return { payload: response };
      },
    },
    loadTable: {
      reducer(state, action: { payload: IWebAnalyticsTableRequest }) {
        const { kind, sortBy, sortDir, search, page, pageSize } =
          action.payload;

        state.tableSettings = { kind, sortBy, sortDir, search, page, pageSize };
        state.tableInProcess = true;
      },
      prepare(request: IWebAnalyticsTableRequest) {
        return { payload: request };
      },
    },
    loadTableResponse: {
      reducer(state, action: { payload: IWebAnalyticsTableResponse }) {
        state.table = action.payload;
        state.tableInProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(response: IWebAnalyticsTableResponse) {
        return { payload: response };
      },
    },
    loadQueryPages: {
      reducer(state, action: { payload: IWebAnalyticsQueryPagesRequest }) {
        state.queryPagesRequest = action.payload;
        state.queryPages = undefined;
        state.queryPagesInProcess = true;
      },
      prepare(request: IWebAnalyticsQueryPagesRequest) {
        return { payload: request };
      },
    },
    loadQueryPagesResponse: {
      reducer(state, action: { payload: IWebAnalyticsQueryPagesResponse }) {
        state.queryPages = action.payload;
        state.queryPagesInProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(response: IWebAnalyticsQueryPagesResponse) {
        return { payload: response };
      },
    },
    closeQueryPages(state) {
      state.queryPagesRequest = undefined;
      state.queryPages = undefined;
      state.queryPagesInProcess = false;
    },
    loadRecommendations: {
      reducer(state, action: { payload: IWebAnalyticsRecommendationsRequest }) {
        state.recommendationsRequest = action.payload;
        state.recommendationsInProcess = true;
      },
      prepare(request: IWebAnalyticsRecommendationsRequest) {
        return { payload: request };
      },
    },
    loadRecommendationsResponse: {
      reducer(
        state,
        action: { payload: IWebAnalyticsRecommendationsResponse },
      ) {
        state.recommendations = action.payload;
        state.recommendationsInProcess = false;
        state.updatedAt = Date.now();
      },
      prepare(response: IWebAnalyticsRecommendationsResponse) {
        return { payload: response };
      },
    },
    updateRecommendation: {
      reducer(
        state,
        action: { payload: IWebAnalyticsUpdateRecommendationRequest },
      ) {
        state.updatingRecommendationUUID = action.payload.uuid;
      },
      prepare(request: IWebAnalyticsUpdateRecommendationRequest) {
        return { payload: request };
      },
    },
    updateRecommendationResponse: {
      reducer(
        state,
        action: { payload: IWebAnalyticsUpdateRecommendationResponse },
      ) {
        const row = state.recommendations?.rows.find(
          ({ uuid }) => uuid === action.payload.uuid,
        );

        if (row) {
          row.status = action.payload.status;
        }

        state.updatingRecommendationUUID = undefined;
        state.updatedAt = Date.now();
      },
      prepare(response: IWebAnalyticsUpdateRecommendationResponse) {
        return { payload: response };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('WebAnalyticsInsights'),
      (state): void => {
        resetLoadingOnRequestFailed(state);
        state.overviewInProcess = false;
        state.tableInProcess = false;
        state.queryPagesInProcess = false;
        state.recommendationsInProcess = false;
        state.updatingRecommendationUUID = undefined;
      },
    );
  },
});

export const {
  loadProviderStatus,
  loadProviderStatusResponse,
  loadOverview,
  loadOverviewResponse,
  loadTable,
  loadTableResponse,
  loadQueryPages,
  loadQueryPagesResponse,
  closeQueryPages,
  loadRecommendations,
  loadRecommendationsResponse,
  updateRecommendation,
  updateRecommendationResponse,
} = webAnalyticsInsightsSlice.actions;
const reducer: Reducer<IWebAnalyticsInsightsState> =
  webAnalyticsInsightsSlice.reducer;

export default reducer;
